import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { TextRunStyleUpdate } from '@/lib/text/types/text-run-style-update'
import type { ShapeTextCaret } from '@/lib/shapes/types/shape-text-caret'
import {
  type ShapeTextFlow,
  type ShapeTextLayoutResult,
  caretIndexAtShapeLocalPoint,
  computeSelectionRects,
} from '@/lib/shapes/shape-text'
import { clientPointToShapeLocal } from '@/lib/shapes/shape-text-hit'
import {
  chainFrameAtClientPoint,
  chainFrameTextLengths,
  getShapeTextFrame,
} from '@/components/app/shapes/shape-text-frame-registry'
import { frameLocalToGlobalOffset } from '@/lib/text-linking/locate-chain-caret-destination'
import type { ChainSelectionExtendUnit } from '@/lib/shapes/shape-text-navigation'
import {
  type MultiClickState,
  granularityForCount,
  isBeyondClickSlop,
  nextMultiClickState,
  resolveClickSelectionRange,
} from '@/lib/shapes/shape-text-multi-click'

export type { ChainSelectionExtendUnit }

/** Chain-global selection endpoints, in the `\n`-inclusive plain-text space each frame counts in. */
export interface ChainTextSelection {
  frameIds: string[]
  /**
   * Per-member text lengths captured when the drag began, in the same order as `frameIds`.
   *
   * Carried on the selection rather than re-measured by the consumer so that slicing it back into
   * per-frame ranges uses exactly the lengths the offsets were computed against. Re-measuring would
   * reintroduce a skew the moment a reflow moved a character across a seam mid-drag.
   */
  frameLengths: number[]
  anchorGlobal: number
  focusGlobal: number
}

/**
 * Once a cross-frame selection is live the textarea is collapsed at the anchor, so **every** shifted
 * navigation key has to be resolved by the chain owner — leaving any of them to the browser would
 * extend from the anchor instead of the focus and visibly reset the selection (WMPCM-9200).
 */
export interface ChainSelectionExtendPayload {
  frameId: string
  /**
   * This frame's own selection offsets, used only to seed a chain selection the first time. Ignored
   * once one exists, because by then the textarea no longer holds the real endpoints.
   */
  anchorLocal: number
  focusLocal: number
  direction: 1 | -1
  unit: ChainSelectionExtendUnit
}

interface UseShapeTextSelectionOptions {
  shape: ShapeData
  layout: ShapeTextLayoutResult
  /**
   * The row grid `layout` was flowed against. Needed only so `activeCaret` can read the thickness of
   * the row it RELOCATES to — see the comment there for why the caret record's own `rowHeight` is
   * the wrong one, and why the grid rather than `layout.chunks` is the right source.
   */
  flow: ShapeTextFlow
  isEditing: boolean
  /** Canvas zoom (doc px → screen px). Used to undo scale/rotation when mapping clicks to carets. */
  canvasZoom: number
  overlayRef: React.MutableRefObject<HTMLDivElement | null>
  textareaRef: React.MutableRefObject<HTMLTextAreaElement | null>
  pendingRunStyleRef: React.MutableRefObject<TextRunStyleUpdate | null>
  /**
   * Every member of this frame's chain in flow order, or `undefined` for a solo frame. Present
   * means a drag may leave this frame (WMPCM-9199).
   */
  chainFrameIds?: string[]
  /** Publishes a chain-global selection upward; called with `null` when the drag collapses. */
  onChainSelectionChange?: (selection: ChainTextSelection | null) => void
  /**
   * This frame's slice of the live chain selection, in local offsets — already intersected by the
   * owner. Drives the highlight so a non-editing member can paint its share.
   */
  chainSelectionRange?: { start: number; end: number } | null
}

export interface UseShapeTextSelectionResult {
  caretIndex: number
  setCaretIndex: React.Dispatch<React.SetStateAction<number>>
  selectionEndIndex: number
  setSelectionEndIndex: React.Dispatch<React.SetStateAction<number>>
  selectionForward: boolean
  setSelectionForward: React.Dispatch<React.SetStateAction<boolean>>
  syncSelectionFromTextarea: () => void
  handlePointerDown: (event: React.PointerEvent<HTMLDivElement>) => void
  handlePointerUp: () => void
  activeCaret: ShapeTextCaret | null
  selectionRects: Array<{ x: number; y: number; width: number; height: number }>
}

export function useShapeTextSelection({
  shape,
  layout,
  flow,
  isEditing,
  canvasZoom,
  overlayRef,
  textareaRef,
  pendingRunStyleRef,
  chainFrameIds,
  onChainSelectionChange,
  chainSelectionRange,
}: UseShapeTextSelectionOptions): UseShapeTextSelectionResult {
  const [caretIndex, setCaretIndex] = useState(0)
  const [selectionEndIndex, setSelectionEndIndex] = useState(0)
  const [selectionForward, setSelectionForward] = useState(true)
  const dragAnchorRef = useRef<number | null>(null)
  // Chain-drag bookkeeping. Refs, not state: these are read from window-level pointer handlers that
  // must see the current value without the listener being torn down and reattached every move.
  const chainDragRef = useRef<{
    frameIds: string[]
    lengths: number[]
    myIndex: number
    anchorGlobal: number
  } | null>(null)
  /** Last chain focus offset actually published, to suppress no-op republishes during a drag. */
  const publishedFocusRef = useRef<number | null>(null)
  /**
   * Live click sequence for the progressive-granularity ladder (WMPCM-2076). A ref for the same
   * reason the drag bookkeeping above is: the window-level move handler updates its `dragged` flag,
   * and re-rendering on every click would buy nothing.
   *
   * One per mounted frame — `ShapeManager` renders a `ShapeTextEditor` per text-bearing shape — so a
   * click that lands on a different frame starts that frame's own sequence at 1 for free.
   */
  const multiClickRef = useRef<MultiClickState | null>(null)
  const chainFrameIdsRef = useRef(chainFrameIds)
  const onChainSelectionChangeRef = useRef(onChainSelectionChange)

  chainFrameIdsRef.current = chainFrameIds
  onChainSelectionChangeRef.current = onChainSelectionChange

  const syncSelectionFromTextarea = useCallback(() => {
    const ta = textareaRef.current
    const s = ta?.selectionStart ?? 0
    const e = ta?.selectionEnd ?? s
    const dir = ta?.selectionDirection ?? 'none'
    const maxIdx = layout.carets.length - 1

    setCaretIndex(Math.max(0, Math.min(s, maxIdx)))
    setSelectionEndIndex(Math.max(0, Math.min(e, maxIdx)))
    setSelectionForward(dir !== 'backward')
  }, [layout.carets.length, textareaRef])

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!isEditing) return
      if (event.button !== 0) {
        event.preventDefault()
        event.stopPropagation()
        // A right/middle press ends any ladder in progress, so it cannot be used to continue one.
        multiClickRef.current = null

        return
      }

      const textarea = textareaRef.current
      const host = overlayRef.current

      if (!textarea || !host) return

      event.preventDefault()
      event.stopPropagation()
      const local = clientPointToShapeLocal(
        event.clientX,
        event.clientY,
        host.getBoundingClientRect(),
        canvasZoom,
        shape.rotation ?? 0,
        shape.width,
        shape.height
      )
      const index = caretIndexAtShapeLocalPoint(layout.carets, local.x, local.y)

      // Progressive selection granularity by click count (WMPCM-2076): 1 caret, 2 word, 3 visual
      // row, 4 paragraph, 5 the whole frame. Tracked by hand because `pointerdown` always reports
      // `event.detail === 0`, and because `preventDefault()` above plus the textarea's
      // `pointer-events: none` mean the browser never runs its own multi-click selection here.
      const clickState = nextMultiClickState(multiClickRef.current, {
        button: event.button,
        clientX: event.clientX,
        clientY: event.clientY,
        time: performance.now(),
      })

      multiClickRef.current = clickState

      const granularity = granularityForCount(clickState?.count ?? 1)

      textarea.focus({ preventScroll: true })
      pendingRunStyleRef.current = null

      if (granularity !== 'caret') {
        const { start, end } = resolveClickSelectionRange({
          granularity,
          // The textarea's value, not the laid-out prefix: on an overflowing frame the flow stops at
          // `consumedLength` but the frame still holds the tail, and a 5-click has to reach it.
          text: textarea.value,
          offset: index,
          carets: layout.carets,
          consumedLength: layout.consumedLength,
        })

        textarea.setSelectionRange(start, end, 'forward')
        // Mirrored into state as well as the textarea, exactly as the caret branch does. The
        // programmatic `setSelectionRange` fires `onSelect` → `syncSelectionFromTextarea`, which
        // recomputes the identical pair — idempotent, not a race. No clamping dance is needed:
        // `layoutShapeTextContent` pads to `text.length + 1` carets over this same string, so
        // `syncSelectionFromTextarea`'s clamp is a no-op for any range resolved here.
        setCaretIndex(start)
        setSelectionEndIndex(end)
        setSelectionForward(true)

        // Anchor deliberately cleared. The window `pointermove` listener below has NO movement
        // threshold — it extends whenever the anchor is set — so a pixel of jitter while releasing a
        // double-click would re-run `extendSelectionToClientPoint` and visibly shrink the word back
        // toward a caret. Nulling it makes that listener and `handlePointerUp`'s guard no-ops.
        // Cost: a drag straight after a multi-click does not extend by word. Deliberately deferred.
        dragAnchorRef.current = null
        chainDragRef.current = null
        publishedFocusRef.current = null
        // Frame-scoped by decision: no click granularity ever produces a `ChainTextSelection`. Drop
        // any range left over from an earlier drag so siblings stop painting it, and so a following
        // keypress routes through the normal textarea path rather than the chain replace.
        // `null` is reference-stable, so React bails out on the repeats.
        if ((chainFrameIdsRef.current?.length ?? 0) > 1) onChainSelectionChangeRef.current?.(null)

        return
      }

      textarea.setSelectionRange(index, index)
      setCaretIndex(index)
      setSelectionEndIndex(index)
      setSelectionForward(true)
      dragAnchorRef.current = index

      // Deliberately NO `setPointerCapture` (WMPCM-9199). Capture retargeted every subsequent
      // pointermove to this frame's own overlay, which — together with sibling frames being
      // `pointerEvents: 'none'` — made it structurally impossible for a drag to reach the next
      // chain member. Window listeners (the same approach `ShapeManager.startInteraction` uses for
      // chrome gestures) see the whole document, and the chain member under the pointer is resolved
      // from the frame registry rather than by DOM hit-testing, so the siblings' `pointerEvents`
      // can stay as they are.
      const ids = chainFrameIdsRef.current

      if (ids && ids.length > 1) {
        const lengths = chainFrameTextLengths(ids)
        const myIndex = ids.indexOf(shape.id)

        if (myIndex >= 0) {
          chainDragRef.current = {
            frameIds: ids,
            lengths,
            myIndex,
            anchorGlobal: frameLocalToGlobalOffset(lengths, myIndex, index),
          }
          // A fresh press collapses any previous cross-frame selection immediately, so the old
          // highlight does not linger on other frames while this drag starts.
          onChainSelectionChangeRef.current?.(null)
        }
      }
    },
    [
      canvasZoom,
      isEditing,
      layout.carets,
      layout.consumedLength,
      overlayRef,
      pendingRunStyleRef,
      shape.height,
      shape.id,
      shape.rotation,
      shape.width,
      textareaRef,
    ]
  )

  /**
   * Extend the selection to the caret under a client point.
   *
   * Two shapes of drag land here. Within this frame it behaves exactly as before — the textarea's
   * own `selectionStart/End` stays the source of truth, so every existing operation that reads it
   * is untouched. Once the pointer reaches another chain member the selection can no longer live in
   * one textarea, so it is published upward as a chain-global range instead and each member paints
   * its own slice (WMPCM-9199).
   */
  const extendSelectionToClientPoint = useCallback(
    (clientX: number, clientY: number) => {
      if (dragAnchorRef.current === null) return
      const textarea = textareaRef.current
      const host = overlayRef.current

      if (!textarea || !host) return

      const anchorIdx = dragAnchorRef.current
      const chainDrag = chainDragRef.current
      const overFrame = chainDrag
        ? chainFrameAtClientPoint(chainDrag.frameIds, clientX, clientY)
        : null

      if (chainDrag && overFrame && overFrame.index !== chainDrag.myIndex) {
        // The pointer is over a sibling. Only that frame can resolve a caret in its own layout.
        const localIdx = getShapeTextFrame(overFrame.frameId)?.caretIndexAtClientPoint(
          clientX,
          clientY
        )

        if (localIdx == null) return

        const focusGlobal = frameLocalToGlobalOffset(chainDrag.lengths, overFrame.index, localIdx)

        // Publishing a fresh object literal on every pointermove would re-render `ShapeManager` and
        // every renderer under it ~60×/s, since React can never bail out on a new reference. Most
        // moves land on the same character, so only publish when the focus actually moved — the
        // same "set state only when a cheap signature changes" rule the visual layer's poll follows.
        if (publishedFocusRef.current !== focusGlobal) {
          publishedFocusRef.current = focusGlobal
          onChainSelectionChangeRef.current?.({
            frameIds: chainDrag.frameIds,
            frameLengths: chainDrag.lengths,
            anchorGlobal: chainDrag.anchorGlobal,
            focusGlobal,
          })
        }

        // The origin frame's own highlight is now drawn from the chain range, so leave the textarea
        // selection collapsed at the anchor rather than letting it paint a competing range.
        textarea.setSelectionRange(anchorIdx, anchorIdx)

        return
      }

      const local = clientPointToShapeLocal(
        clientX,
        clientY,
        host.getBoundingClientRect(),
        canvasZoom,
        shape.rotation ?? 0,
        shape.width,
        shape.height
      )
      const focusIdx = caretIndexAtShapeLocalPoint(layout.carets, local.x, local.y)

      // Back inside the origin frame: drop any cross-frame range so the siblings stop painting.
      // `null` is reference-stable, so React bails out on the repeats by itself.
      if (chainDrag) {
        publishedFocusRef.current = null
        onChainSelectionChangeRef.current?.(null)
      }

      if (focusIdx <= anchorIdx) {
        textarea.setSelectionRange(focusIdx, anchorIdx, 'backward')
        setCaretIndex(focusIdx)
        setSelectionEndIndex(anchorIdx)
        setSelectionForward(false)
      } else {
        textarea.setSelectionRange(anchorIdx, focusIdx, 'forward')
        setCaretIndex(anchorIdx)
        setSelectionEndIndex(focusIdx)
        setSelectionForward(true)
      }
    },
    [canvasZoom, layout.carets, overlayRef, shape.height, shape.rotation, shape.width, textareaRef]
  )

  // No overlay-level `onPointerMove`: the window listener below already covers every move while a
  // drag is live, including the ones inside this overlay. Keeping both ran the caret hit-test twice
  // per pointermove for no benefit.
  const endDrag = useCallback(() => {
    dragAnchorRef.current = null
    chainDragRef.current = null
    publishedFocusRef.current = null
  }, [])

  const handlePointerUp = useCallback(() => {
    if (dragAnchorRef.current === null) return
    endDrag()
  }, [endDrag])

  /**
   * Window-level continuation of the drag. The overlay's own `onPointerMove` only fires while the
   * pointer is inside it, and with pointer capture gone (see `handlePointerDown`) nothing else
   * would follow a drag out of the frame — over a sibling, over the canvas, or off the window.
   */
  useEffect(() => {
    if (!isEditing) return

    const onMove = (event: PointerEvent) => {
      if (dragAnchorRef.current === null) return
      // A press that turns into a drag must not be counted as a click of the next sequence:
      // otherwise a careful drag-select followed by a quick click reads as click 2 and throws the
      // selection away in favour of a word.
      const sequence = multiClickRef.current

      if (
        sequence &&
        !sequence.dragged &&
        isBeyondClickSlop(sequence, event.clientX, event.clientY)
      ) {
        multiClickRef.current = { ...sequence, dragged: true }
      }

      extendSelectionToClientPoint(event.clientX, event.clientY)
    }
    const onUp = () => {
      if (dragAnchorRef.current === null) return
      endDrag()
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)

    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [endDrag, extendSelectionToClientPoint, isEditing])

  // Leaving edit mode ends any click sequence outright, so re-entering never resumes a stale ladder.
  // Covers Escape, blur-exit, a tool change and the chain hand-off alike.
  useEffect(() => {
    if (!isEditing) multiClickRef.current = null
  }, [isEditing])

  const caretDisplayIndex = selectionForward ? selectionEndIndex : caretIndex
  const caretRecord =
    layout.carets[Math.max(0, Math.min(caretDisplayIndex, layout.carets.length - 1))] ?? null
  // A slot-boundary caret stores `x`/`yTop` on the slot it *ends* — that is what lets
  // `computeSelectionRects` close that slot's highlight — while `nextSlotX`/`nextSlotYTop` hold the
  // same offset as the first character of the slot the flow continues into. Draw the bar there: that
  // is where the next typed character lands, and it is what a browser does for a soft wrap. Without
  // this, a click at the start of a wrapped row set the right offset but parked the bar at the end of
  // the row above (WMPCM-8161); the same now holds across a segment gap on a pinched row, where the
  // bar used to jump back across the gap to the previous lobe (WMPCM-8970).
  //
  // `rowHeight` has to be re-read for the same reason `x`/`yTop` do. The record stamps the thickness
  // of the row it ENDS on, and once `applyPerLineLeadingToFlow` gives rows their own pitch that is
  // simply a different row's height — which the caret is now sized from directly (see the ink-box
  // geometry in `shape-text-editor.tsx`), so a stale value shows as a visibly wrong bar. Taken from
  // `flow.rows` and NOT from `layout.chunks`: a slot boundary at the very end of the text lands on a
  // row nothing is painted on, where a chunk lookup would find nothing and quietly fall back to the
  // stale height. The grid has an entry for every row, painted or not.
  const activeCaret =
    caretRecord?.nextSlotX !== undefined && caretRecord.nextSlotYTop !== undefined
      ? {
          ...caretRecord,
          x: caretRecord.nextSlotX,
          yTop: caretRecord.nextSlotYTop,
          rowHeight:
            flow.rows.find((r) => Math.abs(r.yTop - caretRecord.nextSlotYTop!) < 0.01)?.rowHeight ??
            caretRecord.rowHeight,
        }
      : caretRecord

  const selStart = Math.min(caretIndex, selectionEndIndex)
  const selEnd = Math.max(caretIndex, selectionEndIndex)
  // A live cross-frame selection wins over this frame's own textarea range: during one the textarea
  // is deliberately collapsed at the anchor (see `extendSelectionToClientPoint`), and a member that
  // is not the editing frame has no textarea range at all — its share comes only from here.
  const chainStart = chainSelectionRange?.start ?? 0
  const chainEnd = chainSelectionRange?.end ?? 0
  const hasChainRange = chainSelectionRange != null && chainEnd > chainStart
  const selectionRects = useMemo(
    () =>
      hasChainRange
        ? computeSelectionRects(layout.carets, chainStart, chainEnd)
        : computeSelectionRects(layout.carets, selStart, selEnd),
    [chainEnd, chainStart, hasChainRange, layout.carets, selEnd, selStart]
  )

  return {
    caretIndex,
    setCaretIndex,
    selectionEndIndex,
    setSelectionEndIndex,
    selectionForward,
    setSelectionForward,
    syncSelectionFromTextarea,
    handlePointerDown,
    handlePointerUp,
    activeCaret,
    selectionRects,
  }
}
